/**
 * IlMioViaggio - Hub Consultativo del Soggiorno
 * =============================================
 * 
 * Fase 1: Consultazione + Gate di verifica + Richieste sensibili
 * 
 * SOURCE OF TRUTH: 
 * - useJourney hook per dati viaggio
 * - useJourneyDocuments hook per documenti/voucher
 * - useJourneyPolicies hook per regole/policy
 * - useVerifiedAccess hook per stato accesso verificato
 * - useActionRequests hook per richieste sensibili
 * 
 * Tutti i componenti leggono dalla stessa source of truth.
 * 
 * DISTINZIONE ACCESSO:
 * - Consultazione: Accesso libero con Travel Pass code
 * - Azioni sensibili: Richiedono verifica via Magic Link
 * 
 * DISTINZIONE FONDAMENTALE:
 * - STATO ITEM = a che punto è l'elemento (confermato, proposto, etc.)
 * - POLICY = cosa si può fare (modifica, cancellazione, etc.)
 * - STATO REQUEST = stato della richiesta (inviata, in revisione, etc.)
 * - Le richieste NON cambiano automaticamente lo stato dell'item
 */

import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  MapPin, Calendar, Users, Clock, ChevronRight, ArrowLeft,
  Utensils, Home, Wine, Camera, Bike, Heart, RefreshCw,
  AlertCircle, CheckCircle, Play, Star, MessageSquare,
  Map as MapIcon, Phone, Mail, HelpCircle, Send,
  ChevronDown, ChevronUp, Navigation, Coffee, Sparkles,
  FileText, Download, Ticket, Info, ExternalLink,
  Edit3, XCircle, Shield, Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { InteractiveMap } from '../components/InteractiveMap';
import { useJourney, ELEMENT_STATUS, ELEMENT_TYPES } from '../hooks/useJourney';
import { useJourneyDocuments, DOCUMENT_STATUS, DOCUMENT_TYPES, DELIVERY_STATUS } from '../hooks/useJourneyDocuments';
import { useJourneyPolicies } from '../hooks/useJourneyPolicies';
import { useVerifiedAccess } from '../hooks/useVerifiedAccess';
import { useActionRequests } from '../hooks/useActionRequests';
import { CustomerApprovalSection, useCustomerApprovals } from '../components/CustomerApprovalSection';
import { 
  VerificationBanner, 
  VerificationRequestCard,
  AccessLevelBadge 
} from '../components/VerificationGate';
import {
  ItemActionButtons,
  RequestModal,
  RequestsSection
} from '../components/ActionRequestComponents';

// ============== ICON MAPPING ==============

const TYPE_ICONS = {
  accommodation: Home,
  restaurant: Utensils,
  winery: Wine,
  experience: Camera,
  activity: Bike,
  wellness: Heart,
  guide: Users,
  transport: Navigation,
  coffee: Coffee,
  other: MapPin
};

const STATUS_ICONS = {
  draft: Clock,
  confirmed: CheckCircle,
  active: Play,
  completed: Star,
  cancelled: AlertCircle
};

// ============== MAIN COMPONENT ==============

export default function IlMioViaggio() {
  const { code } = useParams();
  const navigate = useNavigate();
  
  // Source of truth unica
  const {
    journey,
    loading,
    error,
    statusConfig,
    elementsByDay,
    mapCommand,
    summary,
    chatContext,
    supportInfo,
    formatDateRange,
    formatGroup,
    getDayDate,
    getElementStatus,
    getElementType,
    reload
  } = useJourney(code);
  
  // Documents source of truth
  const {
    documents,
    availableDocuments,
    availableCount,
    totalCount,
    getDocumentStatus,
    getDocumentType,
    getDeliveryStatus,
    getItemSummary,
    itemHasDocuments,
    loading: documentsLoading
  } = useJourneyDocuments(code);
  
  // Policies source of truth
  const {
    getPolicySummary,
    canModify,
    canCancel,
    loading: policiesLoading
  } = useJourneyPolicies(code);
  
  // Verified access state
  const {
    isVerified,
    accessLevel,
    canAccessSensitiveActions,
    sessionMinutesRemaining,
    sessionToken,
    emailHint,
    requestingLink,
    linkRequested,
    requestMagicLink,
    loading: accessLoading
  } = useVerifiedAccess(code);
  
  // Action requests state
  const {
    requests,
    summary: requestsSummary,
    typeDisplay: requestTypeDisplay,
    statusDisplay: requestStatusDisplay,
    hasPendingRequestForItem,
    createRequest,
    cancelRequest,
    submitting: requestSubmitting,
    reload: reloadRequests
  } = useActionRequests(code, sessionToken);
  
  // Customer approvals state
  const {
    pendingApprovals,
    approvedItems,
    awaitingItems,
    loading: approvalsLoading,
    submitting: approvalsSubmitting,
    approve: approveItem,
    reject: rejectItem,
    reload: reloadApprovals
  } = useCustomerApprovals(code, sessionToken);
  
  // UI state
  const [showMap, setShowMap] = useState(false);
  const [expandedDays, setExpandedDays] = useState({});
  const [showChatShell, setShowChatShell] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  
  // Action request modal state
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestModalData, setRequestModalData] = useState(null);
  
  // Handler per aprire il modal di richiesta
  const handleRequestAction = (itemId, itemTitle, itemType, requestType, policy) => {
    setRequestModalData({
      itemId,
      itemTitle,
      itemType,
      requestType,
      policy
    });
    setRequestModalOpen(true);
  };
  
  // Handler per inviare la richiesta
  const handleSubmitRequest = async (itemId, requestType, message, payload) => {
    const result = await createRequest(itemId, requestType, message, payload);
    if (result?.success) {
      setRequestModalOpen(false);
      setRequestModalData(null);
    }
    return result;
  };

  // Expand all days on first load
  useState(() => {
    if (elementsByDay && Object.keys(expandedDays).length === 0) {
      const days = {};
      Object.keys(elementsByDay).forEach(day => { days[day] = true; });
      setExpandedDays(days);
    }
  }, [elementsByDay]);

  const toggleDay = (day) => {
    setExpandedDays(prev => ({ ...prev, [day]: !prev[day] }));
  };

  // Get status icon
  const StatusIcon = STATUS_ICONS[statusConfig.key] || Clock;

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-umbria-cream to-white flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-umbria-olive mx-auto mb-4" />
          <p className="text-umbria-charcoal">Caricamento del tuo viaggio...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-umbria-cream to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-umbria-charcoal mb-2">
              {error}
            </h2>
            <p className="text-gray-500 mb-6">
              Il link potrebbe essere errato o il viaggio potrebbe non essere disponibile.
            </p>
            <Button onClick={() => navigate('/')}>
              Torna alla Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!journey) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-umbria-cream via-white to-umbria-cream/30">
      
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-md border-b border-umbria-stone/20 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Back to Travel Pass */}
            <Link 
              to={`/viaggio/${code}`}
              className="flex items-center gap-2 text-umbria-muted hover:text-umbria-charcoal transition-colors"
              data-testid="back-to-pass"
            >
              <ArrowLeft size={18} />
              <span className="text-sm">Travel Pass</span>
            </Link>
            
            <div className="flex items-center gap-3">
              {/* Access Level Badge */}
              <AccessLevelBadge isVerified={isVerified} compact />
              
              <Badge className={`${statusConfig.color} border`}>
                <StatusIcon className="h-3 w-3 mr-1" />
                {statusConfig.label}
              </Badge>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowMap(true)}
                className="gap-2"
                data-testid="open-map-btn"
              >
                <MapIcon size={16} />
                <span className="hidden sm:inline">Mappa</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        
        {/* ============== TRIP HEADER ============== */}
        {/* Reads from: journey (source of truth) */}
        <section className="bg-white rounded-2xl shadow-sm border border-umbria-stone/10 overflow-hidden">
          <div className="bg-gradient-to-r from-umbria-olive/10 via-umbria-sage/5 to-transparent p-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <p className="text-sm text-umbria-muted mb-1">Il Mio Viaggio</p>
                <h1 className="text-2xl md:text-3xl font-serif font-semibold text-umbria-charcoal mb-2">
                  {journey.title}
                </h1>
                <p className="text-umbria-muted">{statusConfig.description}</p>
              </div>
              
              {/* Quick Info - from journey.dates and journey.group */}
              <div className="flex flex-wrap gap-4 text-sm">
                {journey.dates?.start && (
                  <div className="flex items-center gap-2 text-umbria-charcoal">
                    <Calendar size={16} className="text-umbria-olive" />
                    <span>{formatDateRange()}</span>
                  </div>
                )}
                {journey.group && (
                  <div className="flex items-center gap-2 text-umbria-charcoal">
                    <Users size={16} className="text-umbria-olive" />
                    <span>{formatGroup()}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Trip Summary - from summary (derived from source of truth) */}
          <div className="px-6 py-4 border-t border-umbria-stone/10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <SummaryItem label="Giorni" value={summary.days || '-'} />
              <SummaryItem label="Esperienze" value={summary.experiences} />
              <SummaryItem label="Base" value={summary.base} />
              <SummaryItem label="Codice" value={summary.code} small />
            </div>
          </div>
        </section>

        {/* ============== VERIFICATION BANNER ============== */}
        {/* Shows access status: consultative vs verified */}
        <VerificationBanner
          isVerified={isVerified}
          sessionMinutesRemaining={sessionMinutesRemaining}
          onRequestVerification={() => setShowVerificationModal(true)}
          loading={accessLoading}
        />

        {/* ============== VERIFICATION MODAL ============== */}
        {showVerificationModal && !isVerified && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="max-w-md w-full">
              <VerificationRequestCard
                emailHint={emailHint}
                onRequestMagicLink={requestMagicLink}
                requesting={requestingLink}
                linkRequested={linkRequested}
              />
              <Button
                variant="ghost"
                className="w-full mt-2 text-white hover:bg-white/10"
                onClick={() => setShowVerificationModal(false)}
              >
                Chiudi
              </Button>
            </div>
          </div>
        )}

        {/* ============== TIMELINE ============== */}
        {/* Reads from: elementsByDay (derived from source of truth) */}
        <section data-testid="trip-timeline">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-umbria-charcoal flex items-center gap-2">
              <Calendar size={20} className="text-umbria-olive" />
              Il tuo itinerario
            </h2>
          </div>
          
          {Object.keys(elementsByDay).length === 0 ? (
            <Card className="bg-amber-50/50 border-amber-100">
              <CardContent className="py-8 text-center">
                <Sparkles className="h-10 w-10 text-amber-400 mx-auto mb-3" />
                <p className="text-umbria-charcoal font-medium">
                  Itinerario in costruzione
                </p>
                <p className="text-sm text-umbria-muted mt-1">
                  Chiara sta lavorando alle proposte per il tuo viaggio
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {Object.entries(elementsByDay)
                .sort(([a], [b]) => Number(a) - Number(b))
                .map(([day, elements]) => (
                  <DayCard
                    key={day}
                    day={Number(day)}
                    elements={elements}
                    expanded={expandedDays[day] !== false}
                    onToggle={() => toggleDay(day)}
                    getDayDate={getDayDate}
                    getElementStatus={getElementStatus}
                    getElementType={getElementType}
                    getItemSummary={getItemSummary}
                    getPolicySummary={getPolicySummary}
                    isVerified={isVerified}
                    hasPendingRequestForItem={hasPendingRequestForItem}
                    onRequestAction={handleRequestAction}
                    onRequestVerification={() => setShowVerificationModal(true)}
                  />
                ))}
            </div>
          )}
        </section>

        {/* ============== CUSTOMER APPROVALS SECTION ============== */}
        {/* Reads from: useCustomerApprovals (source of truth for approvals) */}
        <section data-testid="approvals-section">
          <CustomerApprovalSection
            pendingApprovals={pendingApprovals}
            approvedItems={approvedItems}
            awaitingItems={awaitingItems}
            isVerified={isVerified}
            onApprove={approveItem}
            onReject={rejectItem}
            onRequestVerification={() => setShowVerificationModal(true)}
            submitting={approvalsSubmitting}
            loading={approvalsLoading}
          />
        </section>

        {/* ============== DOCUMENTS SECTION ============== */}
        {/* Reads from: useJourneyDocuments (source of truth for documents) */}
        <section data-testid="documents-section">
          <Card className="bg-white">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText size={18} className="text-umbria-olive" />
                  Documenti del viaggio
                </CardTitle>
                {availableCount > 0 && (
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    {availableCount} disponibil{availableCount === 1 ? 'e' : 'i'}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {documentsLoading ? (
                <div className="flex items-center justify-center py-6">
                  <RefreshCw className="h-5 w-5 animate-spin text-umbria-olive" />
                </div>
              ) : documents.length === 0 ? (
                <div className="text-center py-6 bg-gray-50/50 rounded-lg">
                  <FileText className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-umbria-muted">
                    Nessun documento disponibile al momento
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    I documenti saranno disponibili quando le prenotazioni saranno confermate
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <DocumentRow 
                      key={doc.id} 
                      document={doc}
                      getDocumentStatus={getDocumentStatus}
                      getDocumentType={getDocumentType}
                      getDeliveryStatus={getDeliveryStatus}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        {/* ============== REQUESTS SECTION ============== */}
        {/* Reads from: useActionRequests (source of truth for requests) */}
        <section data-testid="requests-section">
          <RequestsSection
            requests={requests}
            summary={requestsSummary}
            typeDisplay={requestTypeDisplay}
            statusDisplay={requestStatusDisplay}
            isVerified={isVerified}
            onCancel={cancelRequest}
            onRequestVerification={() => setShowVerificationModal(true)}
          />
        </section>

        {/* ============== CHAT SHELL ============== */}
        {/* Context from: chatContext (derived from source of truth) */}
        <section data-testid="chat-shell">
          <Card className="bg-gradient-to-br from-umbria-olive/5 to-transparent border-umbria-olive/20">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-umbria-olive to-umbria-sage flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="text-white" size={22} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-umbria-charcoal mb-1">
                    Chat del viaggio
                  </h3>
                  <p className="text-sm text-umbria-muted mb-4">
                    Hai domande sul tuo viaggio? Chiara è qui per aiutarti.
                    La conversazione riprende da dove l'avevi lasciata.
                  </p>
                  
                  {showChatShell ? (
                    <ChatShellPreview chatContext={chatContext} />
                  ) : (
                    <Button 
                      variant="outline" 
                      className="gap-2 border-umbria-olive/30 hover:bg-umbria-olive/5"
                      onClick={() => setShowChatShell(true)}
                      data-testid="open-chat-shell"
                    >
                      <MessageSquare size={16} />
                      Apri chat
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* ============== SUPPORT SECTION ============== */}
        {/* Reads from: supportInfo (from source of truth) */}
        <section data-testid="support-section">
          <Card className="bg-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <HelpCircle size={18} className="text-umbria-olive" />
                Hai bisogno di assistenza?
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-umbria-muted">
                Il nostro team è disponibile per aiutarti con qualsiasi domanda 
                sul tuo viaggio in Umbria.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-3">
                <a 
                  href={`mailto:${supportInfo.email}`}
                  className="flex items-center gap-3 p-3 rounded-lg border border-umbria-stone/20 hover:bg-umbria-cream/30 transition-colors"
                >
                  <Mail size={18} className="text-umbria-olive" />
                  <div>
                    <p className="text-sm font-medium text-umbria-charcoal">Email</p>
                    <p className="text-xs text-umbria-muted">{supportInfo.email}</p>
                  </div>
                </a>
                
                <a 
                  href={supportInfo.phone ? `tel:${supportInfo.phone}` : '#'}
                  className="flex items-center gap-3 p-3 rounded-lg border border-umbria-stone/20 hover:bg-umbria-cream/30 transition-colors"
                >
                  <Phone size={18} className="text-umbria-olive" />
                  <div>
                    <p className="text-sm font-medium text-umbria-charcoal">Telefono</p>
                    <p className="text-xs text-umbria-muted">{supportInfo.hours}</p>
                  </div>
                </a>
              </div>
              
              <div className="pt-2 border-t border-umbria-stone/10">
                <p className="text-xs text-umbria-muted">
                  Codice viaggio: <code className="bg-umbria-cream px-2 py-0.5 rounded">{code}</code>
                  {' '}— Comunicalo quando ci contatti per assistenza più rapida.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Footer */}
        <footer className="text-center py-6 text-xs text-gray-400">
          <p>Feel Umbria — Il tuo viaggio, la nostra passione</p>
        </footer>
      </main>

      {/* ============== MAP PANEL ============== */}
      {/* Reads from: mapCommand (derived from source of truth) */}
      <InteractiveMap
        isOpen={showMap}
        onClose={() => setShowMap(false)}
        mapCommand={mapCommand}
        userBase={journey?.base || journey?.accommodation_area}
      />
      
      {/* ============== REQUEST MODAL ============== */}
      {/* Creates action requests for verified users */}
      {requestModalData && (
        <RequestModal
          isOpen={requestModalOpen}
          onClose={() => {
            setRequestModalOpen(false);
            setRequestModalData(null);
          }}
          itemId={requestModalData.itemId}
          itemTitle={requestModalData.itemTitle}
          itemType={requestModalData.itemType}
          requestType={requestModalData.requestType}
          policy={requestModalData.policy}
          onSubmit={handleSubmitRequest}
          submitting={requestSubmitting}
        />
      )}
    </div>
  );
}

// ============== SUB-COMPONENTS ==============

function SummaryItem({ label, value, small = false }) {
  return (
    <div>
      <p className="text-xs text-umbria-muted mb-1">{label}</p>
      <p className={`font-semibold text-umbria-charcoal ${small ? 'text-xs font-mono' : 'text-lg'}`}>
        {value}
      </p>
    </div>
  );
}

function DayCard({ 
  day, 
  elements, 
  expanded, 
  onToggle, 
  getDayDate, 
  getElementStatus, 
  getElementType, 
  getItemSummary, 
  getPolicySummary,
  isVerified,
  hasPendingRequestForItem,
  onRequestAction,
  onRequestVerification
}) {
  const dayDate = getDayDate(day);
  
  return (
    <Card className="overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between bg-gradient-to-r from-umbria-olive/5 to-transparent hover:from-umbria-olive/10 transition-colors"
        data-testid={`day-${day}-toggle`}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-umbria-olive/10 flex items-center justify-center">
            <span className="font-semibold text-umbria-olive">{day}</span>
          </div>
          <div className="text-left">
            <p className="font-medium text-umbria-charcoal">Giorno {day}</p>
            {dayDate && (
              <p className="text-xs text-umbria-muted">
                {dayDate.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' })}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            {elements.length} {elements.length === 1 ? 'elemento' : 'elementi'}
          </Badge>
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </button>
      
      {expanded && (
        <div className="divide-y divide-umbria-stone/10">
          {elements.map((element, idx) => (
            <ElementRow 
              key={element.id || idx} 
              element={element}
              getElementStatus={getElementStatus}
              getElementType={getElementType}
              getItemSummary={getItemSummary}
              getPolicySummary={getPolicySummary}
              isVerified={isVerified}
              hasPendingRequest={hasPendingRequestForItem?.(element.id)}
              onRequestAction={onRequestAction}
              onRequestVerification={onRequestVerification}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

function ElementRow({ 
  element, 
  getElementStatus, 
  getElementType, 
  getItemSummary, 
  getPolicySummary,
  isVerified,
  hasPendingRequest,
  onRequestAction,
  onRequestVerification
}) {
  const [showRules, setShowRules] = useState(false);
  const typeConfig = getElementType(element.type);
  const statusConfig = getElementStatus(element.status);
  const TypeIcon = TYPE_ICONS[element.type] || TYPE_ICONS.other;
  
  // Document badge info
  const docSummary = getItemSummary ? getItemSummary(element.id) : null;
  const hasAvailableDocs = docSummary && docSummary.available_documents > 0;
  const hasVoucher = docSummary && docSummary.has_voucher;
  
  // Policy info
  const policy = getPolicySummary ? getPolicySummary(element.id) : null;
  
  return (
    <div className="px-4 py-3" data-testid="element-row">
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="w-9 h-9 rounded-lg bg-umbria-cream flex items-center justify-center flex-shrink-0 mt-0.5">
          <TypeIcon size={18} className="text-umbria-olive" />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-medium text-umbria-charcoal truncate">
                {element.title}
              </p>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-umbria-muted mt-0.5">
                <span>{typeConfig.label}</span>
                {element.location && (
                  <span className="flex items-center gap-1">
                    <MapPin size={10} />
                    {element.location}
                  </span>
                )}
                {element.time && (
                  <span className="flex items-center gap-1">
                    <Clock size={10} />
                    {element.time}
                  </span>
                )}
              </div>
            </div>
            
            {/* Badges */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {/* Document badge - only show if has available documents */}
              {hasAvailableDocs && (
                <Badge 
                  className="bg-blue-50 text-blue-700 text-[10px] px-1.5 py-0.5 gap-0.5"
                  title={hasVoucher ? 'Voucher disponibile' : 'Documenti disponibili'}
                >
                  <FileText size={10} />
                  {docSummary.available_documents}
                </Badge>
              )}
              
              {/* Pending request badge */}
              {hasPendingRequest && (
                <Badge 
                  className="bg-amber-50 text-amber-700 text-[10px] px-1.5 py-0.5 gap-0.5"
                  title="Richiesta in corso"
                >
                  <Clock size={10} />
                </Badge>
              )}
              
              {/* Status Badge */}
              <Badge className={`${statusConfig.color} text-xs`}>
                {statusConfig.label}
              </Badge>
            </div>
          </div>
          
          {/* Note */}
          {element.notes && (
            <p className="text-xs text-umbria-muted mt-2 line-clamp-2">
              {element.notes}
            </p>
          )}
          
          {/* Rules toggle */}
          {policy && (
            <button
              onClick={() => setShowRules(!showRules)}
              className="mt-2 text-xs text-umbria-olive/70 hover:text-umbria-olive flex items-center gap-1 transition-colors"
              data-testid="toggle-rules"
            >
              <Shield size={12} />
              {showRules ? 'Nascondi dettagli' : 'Dettagli e azioni'}
              {showRules ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
          )}
        </div>
      </div>
      
      {/* Rules Panel + Action Buttons */}
      {showRules && policy && (
        <PolicyRulesPanel 
          policy={policy} 
          element={element}
          isVerified={isVerified}
          hasPendingRequest={hasPendingRequest}
          onRequestAction={onRequestAction}
          onRequestVerification={onRequestVerification}
        />
      )}
    </div>
  );
}

/**
 * PolicyRulesPanel - Visualizzazione consultativa delle regole + Azioni
 * 
 * Mostra in modo sobrio e chiaro:
 * - Modifica: consentita / su richiesta / non consentita
 * - Cancellazione: consentita / con condizioni / non rimborsabile
 * - Dopo voucher: come vengono gestite le modifiche
 * - Eccezioni: come vengono gestiti gli imprevisti
 * 
 * + PULSANTI AZIONE (solo nel dettaglio espanso):
 * - Se verificato: Modifica / Cancella / Assistenza
 * - Se policy "not_allowed": trasforma in "Chiedi assistenza"
 * - Se richiesta pending: mostra stato
 */
function PolicyRulesPanel({ 
  policy, 
  element,
  isVerified,
  hasPendingRequest,
  onRequestAction,
  onRequestVerification
}) {
  // Check if modification is allowed
  const modAllowed = policy.modification?.label !== 'Non consentita';
  const cancAllowed = policy.cancellation?.label !== 'Non consentita' && 
                      policy.cancellation?.label !== 'Non rimborsabile';
  
  return (
    <div 
      className="mt-3 ml-12 p-3 bg-umbria-stone/5 rounded-lg border border-umbria-stone/10"
      data-testid="policy-rules-panel"
    >
      <p className="text-xs font-medium text-umbria-charcoal mb-2 flex items-center gap-1.5">
        <Shield size={12} className="text-umbria-olive" />
        Regole del servizio
      </p>
      
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
        {/* Modifica */}
        <div>
          <span className="text-umbria-muted">Modifica:</span>
        </div>
        <div className={policy.modification?.color || ''}>
          <span className="font-medium">{policy.modification?.label || 'N/D'}</span>
          {policy.modification?.hours && (
            <span className="text-umbria-muted ml-1">
              (entro {policy.modification.hours}h)
            </span>
          )}
        </div>
        
        {/* Cancellazione */}
        <div>
          <span className="text-umbria-muted">Cancellazione:</span>
        </div>
        <div className={policy.cancellation?.color || ''}>
          <span className="font-medium">{policy.cancellation?.label || 'N/D'}</span>
          {policy.cancellation?.hours && (
            <span className="text-umbria-muted ml-1">
              (entro {policy.cancellation.hours}h)
            </span>
          )}
        </div>
        
        {/* Post-voucher */}
        <div>
          <span className="text-umbria-muted">Dopo voucher:</span>
        </div>
        <div>
          <span className="text-umbria-charcoal">{policy.post_voucher?.label || 'N/D'}</span>
        </div>
        
        {/* Eccezioni */}
        <div>
          <span className="text-umbria-muted">Imprevisti:</span>
        </div>
        <div>
          <span className="text-umbria-charcoal">{policy.exceptions?.label || 'N/D'}</span>
        </div>
      </div>
      
      {/* User note */}
      {policy.user_note && (
        <p className="mt-2 pt-2 border-t border-umbria-stone/10 text-xs text-umbria-muted italic">
          {policy.user_note}
        </p>
      )}
      
      {/* ========== ACTION BUTTONS SECTION ========== */}
      <div className="mt-3 pt-3 border-t border-umbria-stone/10">
        {!isVerified ? (
          /* Non verificato: invita a verificare */
          <div className="flex items-center gap-2 text-xs text-umbria-muted">
            <Lock size={12} className="text-umbria-olive" />
            <span>Per richiedere modifiche, </span>
            <button 
              onClick={onRequestVerification}
              className="text-umbria-olive hover:underline font-medium"
              data-testid="verify-to-request-btn"
            >
              verifica il tuo accesso
            </button>
          </div>
        ) : hasPendingRequest ? (
          /* Richiesta già in corso */
          <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 p-2 rounded">
            <Clock size={12} />
            <span>Hai già una richiesta in corso per questo elemento</span>
          </div>
        ) : (
          /* Verificato: mostra pulsanti azione */
          <div className="space-y-2">
            <p className="text-xs text-umbria-muted mb-2">
              Hai bisogno di modificare questa prenotazione?
            </p>
            <div className="flex flex-wrap gap-2">
              {/* Modifica: se non consentita, diventa "Chiedi assistenza per modifica" */}
              {modAllowed ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs h-7 px-2.5"
                  onClick={() => onRequestAction(
                    element.id, 
                    element.title, 
                    element.type, 
                    'change_request', 
                    policy
                  )}
                  data-testid="request-change-btn"
                >
                  <Edit3 size={12} className="mr-1" />
                  Richiedi modifica
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs h-7 px-2.5 text-amber-600 border-amber-200 hover:bg-amber-50"
                  onClick={() => onRequestAction(
                    element.id, 
                    element.title, 
                    element.type, 
                    'support_request', 
                    policy
                  )}
                  data-testid="request-change-help-btn"
                >
                  <HelpCircle size={12} className="mr-1" />
                  Assistenza modifica
                </Button>
              )}
              
              {/* Cancellazione: se non consentita, diventa "Chiedi assistenza per cancellazione" */}
              {cancAllowed ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs h-7 px-2.5 text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50"
                  onClick={() => onRequestAction(
                    element.id, 
                    element.title, 
                    element.type, 
                    'cancellation_request', 
                    policy
                  )}
                  data-testid="request-cancel-btn"
                >
                  <XCircle size={12} className="mr-1" />
                  Richiedi cancellazione
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs h-7 px-2.5 text-amber-600 border-amber-200 hover:bg-amber-50"
                  onClick={() => onRequestAction(
                    element.id, 
                    element.title, 
                    element.type, 
                    'support_request', 
                    policy
                  )}
                  data-testid="request-cancel-help-btn"
                >
                  <HelpCircle size={12} className="mr-1" />
                  Assistenza cancellazione
                </Button>
              )}
              
              {/* Assistenza generica sempre disponibile */}
              <Button
                size="sm"
                variant="ghost"
                className="text-xs h-7 px-2.5"
                onClick={() => onRequestAction(
                  element.id, 
                  element.title, 
                  element.type, 
                  'support_request', 
                  policy
                )}
                data-testid="request-support-btn"
              >
                <HelpCircle size={12} className="mr-1" />
                Altro
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DocumentRow({ document, getDocumentStatus, getDocumentType, getDeliveryStatus }) {
  const statusConfig = getDocumentStatus(document.status);
  const typeConfig = getDocumentType(document.doc_type);
  const deliveryConfig = getDeliveryStatus(document.delivery_status);
  
  // Icon mapping for document types
  const typeIcons = {
    voucher: Ticket,
    booking_summary: FileText,
    ticket: Ticket,
    info_sheet: Info,
    receipt_placeholder: FileText
  };
  const TypeIcon = typeIcons[document.doc_type] || FileText;
  
  // Can download?
  const canDownload = document.is_downloadable && document.file_url && statusConfig.showDownload;
  
  return (
    <div 
      className="flex items-center gap-3 p-3 rounded-lg border border-umbria-stone/10 bg-white hover:bg-umbria-cream/20 transition-colors"
      data-testid="document-row"
    >
      {/* Icon */}
      <div className="w-10 h-10 rounded-lg bg-umbria-cream flex items-center justify-center flex-shrink-0">
        <TypeIcon size={18} className="text-umbria-olive" />
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-umbria-charcoal text-sm truncate">
          {document.title}
        </p>
        <div className="flex items-center gap-2 text-xs text-umbria-muted mt-0.5">
          <span>{typeConfig.label}</span>
          {document.version > 1 && (
            <span className="text-blue-600">v{document.version}</span>
          )}
          {document.issued_at && (
            <span>
              {new Date(document.issued_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}
            </span>
          )}
          {/* Delivery status indicator */}
          {deliveryConfig.label && (
            <span className={`flex items-center gap-1 ${deliveryConfig.color}`}>
              <Mail size={10} />
              {deliveryConfig.label}
            </span>
          )}
        </div>
      </div>
      
      {/* Status + Download */}
      <div className="flex items-center gap-2">
        <Badge className={`${statusConfig.color} text-xs`}>
          {statusConfig.label}
        </Badge>
        
        {canDownload ? (
          <a
            href={document.file_url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-lg hover:bg-umbria-olive/10 text-umbria-olive transition-colors"
            title="Scarica documento"
            data-testid="document-download"
          >
            <Download size={16} />
          </a>
        ) : statusConfig.showDownload === false ? null : (
          <div className="p-2 text-gray-300" title="Non ancora disponibile">
            <Download size={16} />
          </div>
        )}
      </div>
    </div>
  );
}

function ChatShellPreview({ chatContext }) {
  // La chat shell riceve il context dal journey
  // In futuro userà questi dati per inizializzare la conversazione
  
  return (
    <div className="bg-white rounded-xl border border-umbria-stone/20 p-4">
      <div className="space-y-3 mb-4">
        <div className="flex gap-3">
          <div className="w-8 h-8 rounded-full bg-umbria-olive/20 flex items-center justify-center">
            <span className="text-xs font-medium text-umbria-olive">C</span>
          </div>
          <div className="flex-1 bg-umbria-cream/50 rounded-lg p-3">
            <p className="text-sm text-umbria-charcoal">
              Ciao! Sono Chiara. Vedo che stai pianificando 
              {chatContext?.title ? ` "${chatContext.title}"` : ' il tuo viaggio'}.
              {chatContext?.base && ` La tua base sarà a ${chatContext.base}.`}
              {' '}Come posso aiutarti?
            </p>
          </div>
        </div>
      </div>
      
      <div className="flex gap-2">
        <input
          type="text"
          placeholder="Scrivi un messaggio..."
          className="flex-1 px-4 py-2 rounded-lg border border-umbria-stone/30 text-sm focus:outline-none focus:ring-2 focus:ring-umbria-olive/30"
          disabled
        />
        <Button size="sm" disabled className="gap-2">
          <Send size={14} />
        </Button>
      </div>
      
      <p className="text-xs text-umbria-muted mt-3 text-center">
        Chat completa disponibile nella prossima fase
      </p>
    </div>
  );
}
